import { WebDashboardContentSpec } from "@coeff/agent-types";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";

import {
  DashboardWindow,
  evalAndCallAsync,
  normalizeSlicerStateForRealm,
  toBareDate,
} from "./dashboardHelpers";
import {
  ALL_TIME_END,
  ALL_TIME_START,
  DateRangeSelection,
  resolveSelection,
} from "./dateRangeSelection";
import { runtimeInitFingerprint, slicerDefaultsFingerprint } from "./runtimeFingerprints";
import {
  buildStickySnapshot,
  getStickyStorage,
  loadStickyFilters,
  saveStickyFilters,
} from "./slicerStickyState";
import { normalizeSelectOptions, normalizeSelectValues } from "./slicerValues";
import { RunSQLFn as RunSQLFnType } from "./useRunSQL";
import { useViewTransitionStateUpdate } from "./useViewTransitionStateUpdate";
import { addPerfBreadcrumb, LOAD_SPAN_OP, recordCompletedSpan } from "./webDashboardPerf";

// --------------------------------
// Types
// --------------------------------

export type RunSQLFn = RunSQLFnType;

export interface DateRange {
  // "All time" selections arrive as a sentinel-wide window (1900-01-01 to 9999-12-31)
  // so date filters match every row; dates are always concrete Date objects.
  startDate: Date;
  endDate: Date;
}

export type SlicerValue = string | string[] | DateRange;

export type SlicerState = Record<string, SlicerValue>;

export type CleanupFunction = () => void | Promise<void>;

export type RenderFunction = (
  cardElement: HTMLElement,
  slicerState: SlicerState
) => Promise<CleanupFunction | void> | CleanupFunction | void;

export interface SelectOption {
  value: string;
  label: string;
}

/**
 * Normalize the result of an AI-generated date range function. The documented shape is
 * { startDate, endDate } but generated functions sometimes return { start, end }.
 */
export const normalizeDateRange = (value: unknown): DateRange => {
  const v = value as {
    startDate?: Date | string;
    endDate?: Date | string;
    start?: Date | string;
    end?: Date | string;
  } | null;
  const rawStart = v?.startDate ?? v?.start;
  const rawEnd = v?.endDate ?? v?.end;
  if (rawStart == null || rawEnd == null) {
    throw new Error("Date range function must return { startDate, endDate }");
  }
  let startDate = toBareDate(rawStart);
  let endDate = toBareDate(rawEnd);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    throw new Error("Date range function returned invalid dates");
  }
  // A legacy get_default_value_js_function can resolve to the Unix epoch (Jan 1 1970) when it
  // calls SQLite DATE() on a non-ISO date column: DATE() returns NULL and new Date(null) is
  // epoch. Treat an epoch endpoint as an open bound (sentinel) so the picker renders it as
  // "Up to <end>" / "All time" instead of a literal "Jan 1, 1970"; the wide filter is a
  // harmless superset.
  if (startDate.getFullYear() <= 1970) {
    startDate = new Date(ALL_TIME_START.getTime());
  }
  if (endDate.getFullYear() <= 1970) {
    endDate = new Date(ALL_TIME_END.getTime());
  }
  return { startDate, endDate };
};

/**
 * A single-select slicer's literal `default_value`, or null when the JS default should run.
 *
 * A literal outlives the data it was saved against, so it is only honoured while it is still
 * one of the resolved options — an unknown value would select nothing and render every
 * dependent block empty. With no options to check against (a static-option or misconfigured
 * slicer) the literal is taken at face value; there is nothing better to compare it to.
 */
export const resolveLiteralSingleDefault = (
  literal: string | null | undefined,
  options: SelectOption[]
): string | null => {
  if (!literal) {
    return null;
  }
  if (options.length === 0) {
    return literal;
  }
  return options.some(o => o.value === literal) ? literal : null;
};

/**
 * A multi-select slicer's literal `default_value`, or null when the JS default should run.
 *
 * Values missing from the data are dropped rather than failing the whole default, so a saved
 * "West, East, North" still opens on the two that remain. An empty saved list is a real
 * choice (start with nothing selected) and is kept; only a list that had values and lost all
 * of them falls back to the JS function.
 */
export const resolveLiteralMultiDefault = (
  literal: string[] | null | undefined,
  options: SelectOption[]
): string[] | null => {
  if (!Array.isArray(literal)) {
    return null;
  }
  if (literal.length === 0 || options.length === 0) {
    return literal;
  }
  const kept = literal.filter(v => options.some(o => o.value === v));
  return kept.length > 0 ? kept : null;
};

export interface MaterializedTempTableStatement {
  sql: string;
  tableName: string;
}

export interface DashboardRuntimeContext {
  slicerState: SlicerState;
  defaultSlicerState: SlicerState;
  allTimeDateRanges: Record<string, DateRange>;
  resolvedOptions: Record<string, SelectOption[]>;
  /**
   * Structured intent behind each date filter's current value ("Last 30 days"), keyed by slicer
   * name. Seeded from the spec's `default_selection` and replaced when the viewer applies a new
   * choice. Anything persisting a date choice stores this rather than the resolved dates, which
   * are only true for the day they were computed.
   */
  dateRangeSelections: Record<string, DateRangeSelection | null>;
  setSlicerValue: (
    slicerName: string,
    value: SlicerValue,
    selection?: DateRangeSelection | null
  ) => void;
  resetSlicers: () => void;
  isSlicersActive: boolean;
  runSQL: RunSQLFn;
  registerBlockCleanup: (blockId: string, cleanup: CleanupFunction) => void;
  executeBlockRender: (
    blockId: string,
    renderFn: RenderFunction,
    cardElement: HTMLElement
  ) => Promise<void>;
  refreshBlock: (blockId: string) => void;
  refreshAllBlocks: () => void;
  isInitialized: boolean;
  /** Map of slicer name -> error message for slicers that failed to initialize. */
  slicerInitErrors: Record<string, string>;
}

// --------------------------------
// Hook
// --------------------------------

interface UseDashboardRuntimeOptions {
  contentSpec: WebDashboardContentSpec;
  runSQL: RunSQLFn;
  onBlockRefresh?: (blockId: string) => void;
  targetWindow: DashboardWindow;
  /** When false, skip all SQL initialization (SQLite not ready yet) */
  enabled?: boolean;
  /** When true, materialize supported CREATE TEMP VIEW statements as temp tables. */
  materializeTempViews?: boolean;
  /** Email of the currently logged-in viewer (null for anonymous/public dashboards) */
  userEmail?: string | null;
  /** Dashboard id, used to tag perf spans/breadcrumbs so they're filterable in Sentry. */
  dashboardId?: string;
}

const TEMP_VIEW_PREFIX_PATTERN =
  /^(\s*)CREATE\s+TEMP(?:ORARY)?\s+VIEW\s+((?:IF\s+NOT\s+EXISTS\s+)?(?:"[^"]+"|`[^`]+`|\[[^\]]+\]|\w+))(\s+AS\b)/i;

function quoteSQLiteIdentifier(identifier: string): string {
  return `"${identifier.replace(/"/g, '""')}"`;
}

// Rows are inserted in chunks to keep each INSERT statement (and its bound
// parameter count) well within SQLite limits for large derived tables.
const JS_TRANSFORM_INSERT_CHUNK = 500;

// Coerce a JS value produced by a transform into something SQLite can bind.
// Objects/arrays are JSON-stringified; booleans become 0/1; null/undefined -> null.
function coerceCellForSQLite(value: unknown): string | number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number" || typeof value === "string") {
    return value;
  }
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }
  return JSON.stringify(value);
}

// Materialize the rows returned by a JS table transform into a fresh TEMP TABLE
// so blocks/slicers can query it like any other table. The column set is the
// union of keys across rows (first-seen order); columns are typeless (BLOB
// affinity) since transform output is dynamically typed.
//
// Every statement is schema-qualified with `temp.` (SQLite has no DROP TEMP TABLE).
// Unqualified DDL resolves temp-then-main, so on the FIRST materialization of a page
// load — when no temp table exists yet — a bare `DROP TABLE IF EXISTS out` drops
// `main.out` from the loaded combined DB. A transform whose output_table_name collides
// with a detected table (both are named after tabs/columns, so this happens) would then
// silently serve transform rows to any block reading the original table. Mirrors
// _materialize_js_table_transforms_on_conn on the server, which qualifies for this reason.
async function materializeRowsAsTempTable(
  runSQL: RunSQLFn,
  tableName: string,
  rows: unknown
): Promise<void> {
  const quotedTable = `temp.${quoteSQLiteIdentifier(tableName)}`;
  await runSQL(`DROP TABLE IF EXISTS ${quotedTable};`);

  if (!Array.isArray(rows) || rows.length === 0) {
    // Create an empty placeholder so a block querying this table sees no rows
    // rather than a "no such table" hard error.
    await runSQL(`CREATE TEMP TABLE ${quotedTable} (_empty);`);
    return;
  }

  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of rows as Record<string, unknown>[]) {
    for (const key of Object.keys(row ?? {})) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
  }
  if (columns.length === 0) {
    await runSQL(`CREATE TEMP TABLE ${quotedTable} (_empty);`);
    return;
  }

  const columnList = columns.map(quoteSQLiteIdentifier).join(", ");
  await runSQL(`CREATE TEMP TABLE ${quotedTable} (${columnList});`);

  const typedRows = rows as Record<string, unknown>[];
  for (let i = 0; i < typedRows.length; i += JS_TRANSFORM_INSERT_CHUNK) {
    const chunk = typedRows.slice(i, i + JS_TRANSFORM_INSERT_CHUNK);
    const rowPlaceholder = `(${columns.map(() => "?").join(", ")})`;
    const placeholders = chunk.map(() => rowPlaceholder).join(", ");
    const bindings: (string | number | null)[] = [];
    for (const row of chunk) {
      for (const col of columns) {
        bindings.push(coerceCellForSQLite(row?.[col]));
      }
    }
    await runSQL(`INSERT INTO ${quotedTable} (${columnList}) VALUES ${placeholders};`, bindings);
  }
}

export function getMaterializedTempTableStatement(
  sql: string
): MaterializedTempTableStatement | null {
  const match = sql.match(TEMP_VIEW_PREFIX_PATTERN);
  if (!match) {
    return null;
  }

  const tableNameMatch = match[2].match(/(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|(\w+))$/);
  if (!tableNameMatch) {
    return null;
  }

  const tableName =
    tableNameMatch[1] ?? tableNameMatch[2] ?? tableNameMatch[3] ?? tableNameMatch[4];

  return {
    sql: sql.replace(
      TEMP_VIEW_PREFIX_PATTERN,
      () => `${match[1]}CREATE TEMP TABLE ${match[2]}${match[3]}`
    ),
    tableName,
  };
}

export function useDashboardRuntime({
  contentSpec,
  runSQL,
  onBlockRefresh,
  targetWindow,
  enabled = true,
  materializeTempViews = false,
  userEmail,
  dashboardId,
}: UseDashboardRuntimeOptions): DashboardRuntimeContext {
  const [defaultSlicerState, setDefaultSlicerState] = useState<SlicerState>({});
  // The defaults the last init produced, read when the next init seeds state: a slicer still
  // equal to its previous default is "untouched" and must follow the new one (e.g. a refresh
  // adds a region to a default_all slicer) instead of keeping a now-stale value.
  const defaultSlicerStateRef = useRef<SlicerState>({});
  useEffect(() => {
    defaultSlicerStateRef.current = defaultSlicerState;
  }, [defaultSlicerState]);
  const [allTimeDateRanges, setAllTimeDateRanges] = useState<Record<string, DateRange>>({});
  const [resolvedOptions, setResolvedOptions] = useState<Record<string, SelectOption[]>>({});
  const [dateRangeSelections, setDateRangeSelections] = useState<
    Record<string, DateRangeSelection | null>
  >({});
  // The spec's configured selections, so a reset restores intent and not just dates.
  const defaultDateRangeSelectionsRef = useRef<Record<string, DateRangeSelection | null>>({});

  const [slicerState, setSlicerState] = useState<SlicerState>({});
  const [isSlicersActive, setIsSlicersActive] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [slicerInitErrors, setSlicerInitErrors] = useState<Record<string, string>>({});

  // Which dashboard the committed slicer state belongs to. The sticky-filters persist effect
  // gates on this rather than isInitialized: on an in-place dashboard switch there is one
  // commit where the effect's closure still holds the PREVIOUS dashboard's slicerState next
  // to the NEW dashboardId, and writing that mix would stamp one dashboard's filters onto
  // another's storage key. A ref (not state) because it must go null synchronously the moment
  // (re)init starts.
  const stickyDashboardIdRef = useRef<string | null>(null);

  // The init effect keys on these VALUES rather than on contentSpec object identity: every
  // manual edit and save replaces the spec, and rebuilding the runtime for edits it doesn't
  // care about is what made e.g. "save filters as default" flash every block back to a
  // skeleton. Defaults changes are reconciled in place (effect below); initNonce is that
  // reconciler's escape hatch back to a full rebuild when a default needs SQL to resolve.
  const initFingerprint = useMemo(() => runtimeInitFingerprint(contentSpec), [contentSpec]);
  const defaultsFingerprint = useMemo(
    () => slicerDefaultsFingerprint(contentSpec.slicers ?? []),
    [contentSpec.slicers]
  );
  const [initNonce, setInitNonce] = useState(0);
  // The defaults fingerprint the current defaultSlicerState was computed from — written by
  // whichever of init/reconciler last produced it, read by the reconciler to decide whether
  // there is anything to do.
  const appliedDefaultsFingerprintRef = useRef<string | null>(null);

  // When defaultSlicerState changes (e.g., new slicers are added via editor), merge new slicers into state
  useEffect(() => {
    setSlicerState(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      for (const slicerName of Object.keys(defaultSlicerState)) {
        if (!(slicerName in prev)) {
          updated[slicerName] = defaultSlicerState[slicerName];
          hasChanges = true;
        }
      }
      return hasChanges ? updated : prev;
    });
  }, [defaultSlicerState]);

  const cleanupFunctionsRef = useRef<Record<string, CleanupFunction>>({});

  const renderFunctionsRef = useRef<
    Record<string, { renderFn: RenderFunction; cardElement: HTMLElement }>
  >({});

  const [blockRefreshTriggers, setBlockRefreshTriggers] = useState<Record<string, number>>({});

  const startViewTransition = useViewTransitionStateUpdate(targetWindow.document);

  // Initialize temp views.
  //
  // INVARIANT: this effect re-runs on initFingerprint, a value projection of the contentSpec
  // fields it reads (see runtimeInitFingerprint). Reading a new contentSpec field here
  // requires adding it to the fingerprint, or edits to that field will silently skip the
  // rebuild they need. Slicer defaults are the deliberate exception — the reconciler effect
  // below applies those without tearing the runtime down.
  useEffect(() => {
    const tempViewStatements = contentSpec.create_temp_view_sql_statements || [];

    if (!enabled) {
      setIsInitialized(false);
      stickyDashboardIdRef.current = null;
      return;
    }

    let cancelled = false;

    const initializeTempViews = async () => {
      setIsInitialized(false);
      stickyDashboardIdRef.current = null;

      // Instrumentation: the whole runtime-init flow gates `isInitialized`, during
      // which every data block shows a skeleton (looks blank). Time each phase so a
      // slow/blank load can be attributed (temp-view build vs serial slicer init).
      const runtimeInitStart = Date.now();
      const numSlicers = (contentSpec.slicers ?? []).length;
      let tempObjectsCreated = 0;
      let tempObjectsMaterialized = 0;

      // Drop existing temp views and tables first so runtime materialization can
      // switch an object between VIEW and TABLE across dashboard edits.
      const dropStart = Date.now();
      try {
        const tempViewsResult = await runSQL(
          "SELECT name FROM sqlite_temp_master WHERE type = 'view';"
        );
        const tempViewNames = tempViewsResult.map(r => r.name as string);

        let consecutiveFailedDrops = 0;
        const viewsToDrops = [...tempViewNames];

        while (viewsToDrops.length > 0) {
          if (consecutiveFailedDrops >= viewsToDrops.length) {
            console.error("Failed to drop all temp views after N consecutive attempts.");
            break;
          }
          const viewName = viewsToDrops.shift()!;
          try {
            await runSQL(`DROP VIEW IF EXISTS ${quoteSQLiteIdentifier(viewName)};`);
            consecutiveFailedDrops = 0;
          } catch {
            viewsToDrops.push(viewName);
            consecutiveFailedDrops++;
          }
        }

        const tempTablesResult = await runSQL(
          "SELECT name FROM sqlite_temp_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%';"
        );
        for (const tableName of tempTablesResult.map(r => r.name as string)) {
          // Names come from sqlite_temp_master so temp always resolves first here, but stay
          // qualified regardless: an unqualified drop of a name shared with a main table is
          // one refactor away from deleting the main one.
          await runSQL(`DROP TABLE IF EXISTS temp.${quoteSQLiteIdentifier(tableName)};`);
        }
      } catch {
        // sqlite_temp_master might not exist yet
      }
      const dropMs = Date.now() - dropStart;
      addPerfBreadcrumb("drop_existing_objects", { dashboard_id: dashboardId, ms: dropMs });

      // Run JS table transforms BEFORE temp views: reshape raw-tab blobs (or other
      // base tables) into clean derived TEMP TABLEs. They run before temp views so a
      // temp view can standardize a transform's output, and before slicers/blocks
      // which query the derived tables. Each transform is `async (runSQL) => rows`;
      // one that throws is logged and skipped (its dependent block shows its own
      // fallback) rather than failing the whole runtime init.
      const jsTableTransforms = contentSpec.js_table_transforms ?? [];
      if (jsTableTransforms.length > 0) {
        const transformStart = Date.now();
        let transformTablesCreated = 0;
        for (const transform of jsTableTransforms) {
          if (cancelled) {
            return;
          }
          try {
            const rows = await evalAndCallAsync<unknown>(transform.js, runSQL);
            await materializeRowsAsTempTable(runSQL, transform.output_table_name, rows);
            transformTablesCreated += 1;
          } catch (error) {
            console.error(
              `Failed to run js_table_transform '${transform.name}' -> ` +
                `'${transform.output_table_name}':`,
              error
            );
            Sentry.captureException(error, {
              tags: { feature: "web_dashboard", errorType: "js_table_transform" },
              extra: {
                dashboardId,
                transformName: transform.name,
                outputTableName: transform.output_table_name,
              },
            });
            // Blocks querying this table must see zero rows, not "no such table".
            try {
              await materializeRowsAsTempTable(runSQL, transform.output_table_name, []);
            } catch {
              // best-effort placeholder
            }
          }
        }
        if (cancelled) {
          return;
        }
        addPerfBreadcrumb("create_js_table_transforms", {
          dashboard_id: dashboardId,
          ms: Date.now() - transformStart,
          created: transformTablesCreated,
          total: jsTableTransforms.length,
        });
      }

      // Create new temp SQL objects. When the experiment is enabled, materialize
      // supported CREATE TEMP VIEW statements as temp tables so repeated block
      // queries do not re-evaluate the same standardization SQL.
      const createStart = Date.now();
      for (const sql of tempViewStatements) {
        if (cancelled) {
          return;
        }
        try {
          const materializedTable = materializeTempViews
            ? getMaterializedTempTableStatement(sql)
            : null;
          if (materializedTable) {
            try {
              await runSQL(materializedTable.sql);
              tempObjectsCreated += 1;
              tempObjectsMaterialized += 1;
              continue;
            } catch {
              try {
                // temp.-qualified: the CREATE above just failed, so a temp table of this
                // name may not exist, and an unqualified drop would fall through to
                // main.<name> and remove a real table from the loaded combined DB.
                await runSQL(
                  `DROP TABLE IF EXISTS temp.${quoteSQLiteIdentifier(materializedTable.tableName)};`
                );
              } catch (dropError) {
                console.warn(
                  "Failed to drop materialized temp table before view fallback:",
                  dropError
                );
              }
            }
          }
          await runSQL(sql);
          tempObjectsCreated += 1;
        } catch (error) {
          console.error("Failed to initialize temp SQL object:", error);
        }
      }

      if (cancelled) {
        return;
      }
      const createMs = Date.now() - createStart;
      addPerfBreadcrumb("create_temp_objects", {
        dashboard_id: dashboardId,
        ms: createMs,
        created: tempObjectsCreated,
        materialized: tempObjectsMaterialized,
        materialize_temp_views: materializeTempViews,
      });

      // Compute slicer defaults, all-time ranges, and dynamic options from the data.
      // These run serially through the single SQL worker queue and also gate
      // `isInitialized`, so a slow slicer query blocks the entire dashboard.
      const slicerInitStart = Date.now();
      const newDefaultSlicerState: SlicerState = {};
      const newAllTimeDateRanges: Record<string, DateRange> = {};
      const newResolvedOptions: Record<string, SelectOption[]> = {};
      const newSlicerInitErrors: Record<string, string> = {};
      const newDateRangeSelections: Record<string, DateRangeSelection | null> = {};

      for (const slicer of contentSpec.slicers ?? []) {
        try {
          if (slicer.type === "date_range") {
            // A structured default_selection (e.g. {kind:"last", n:30, unit:"day", ...}) is
            // preferred: it is re-resolved from the current date on every load. Fall back to
            // the legacy get_default_value_js_function only when it is absent (data-derived
            // defaults). get_all_time_date_range_js_function is deprecated and no longer read.
            const selection = slicer.default_selection;
            // Data-derived defaults (get_default_value_js_function) have no structured intent,
            // so they stay null: saving one back as a default would pin its literal dates.
            newDateRangeSelections[slicer.name] = selection ?? null;
            if (selection) {
              newDefaultSlicerState[slicer.name] = resolveSelection(selection);
            } else if (slicer.get_default_value_js_function) {
              const def = await evalAndCallAsync<DateRange>(
                slicer.get_default_value_js_function,
                runSQL
              );
              newDefaultSlicerState[slicer.name] = normalizeDateRange(def);
            } else {
              // Neither a structured default_selection nor a data-derived default function
              // (both are optional): default to the open "All time" sentinel window so
              // dependent blocks still receive a usable {startDate, endDate}.
              newDefaultSlicerState[slicer.name] = {
                startDate: new Date(ALL_TIME_START.getTime()),
                endDate: new Date(ALL_TIME_END.getTime()),
              };
            }
          } else if (slicer.type === "personalized_view") {
            const value = userEmail ?? "";
            newDefaultSlicerState[slicer.name] = value;
            if (slicer.get_options_js_function) {
              try {
                newResolvedOptions[slicer.name] = await evalAndCallAsync<SelectOption[]>(
                  slicer.get_options_js_function,
                  runSQL
                );
              } catch (e) {
                console.error("personalized_view options error", e);
              }
            }
          } else {
            if (slicer.get_options_js_function) {
              // NULL / empty values collapse into one "(Blank)" option here, so every consumer
              // (picker, defaults, sticky state) sees the same list.
              newResolvedOptions[slicer.name] = normalizeSelectOptions(
                await evalAndCallAsync<SelectOption[]>(slicer.get_options_js_function, runSQL)
              );
            }
            if (slicer.type === "multi_select") {
              const multiOptions = newResolvedOptions[slicer.name] ?? [];
              // default_all is intent ("everything, including what shows up later"), resolved
              // against this load's options rather than a list frozen at save time.
              const literal = slicer.default_all
                ? multiOptions.map(o => o.value)
                : resolveLiteralMultiDefault(slicer.default_value, multiOptions);
              if (literal !== null) {
                newDefaultSlicerState[slicer.name] = literal;
              } else {
                const defaultValues = await evalAndCallAsync<string[]>(
                  slicer.get_default_value_js_function,
                  runSQL
                );
                newDefaultSlicerState[slicer.name] = normalizeSelectValues(defaultValues);
              }
            } else {
              // Static `options` is deprecated but still what an older single-select renders
              // from, so validate the literal against whichever list this slicer actually uses.
              const literal = resolveLiteralSingleDefault(
                slicer.default_value,
                newResolvedOptions[slicer.name] ?? slicer.options ?? []
              );
              newDefaultSlicerState[slicer.name] =
                literal ??
                (await evalAndCallAsync<string>(slicer.get_default_value_js_function, runSQL));
            }
          }
        } catch (error) {
          console.error(`Failed to initialize slicer ${slicer.name}:`, error);
          Sentry.captureException(error, {
            tags: { feature: "web_dashboard", errorType: "slicer_init" },
            extra: { slicerName: slicer.name, slicerType: slicer.type },
          });
          newSlicerInitErrors[slicer.name] = error instanceof Error ? error.message : String(error);
          // A date_range slicer must always have a usable default so blocks that read
          // slicerState[name].startDate/.endDate still render. Fall back to the open
          // sentinel window ("All time") when its default function failed.
          if (slicer.type === "date_range" && newDefaultSlicerState[slicer.name] === undefined) {
            newDefaultSlicerState[slicer.name] = {
              startDate: new Date(ALL_TIME_START.getTime()),
              endDate: new Date(ALL_TIME_END.getTime()),
            };
          }
        }
      }
      const slicerInitMs = Date.now() - slicerInitStart;
      addPerfBreadcrumb("init_slicers", {
        dashboard_id: dashboardId,
        ms: slicerInitMs,
        num_slicers: numSlicers,
        num_errors: Object.keys(newSlicerInitErrors).length,
      });

      if (!cancelled) {
        // Sticky filters: the viewer's last choices for this dashboard, restored on top of
        // the defaults. Values are re-validated against THIS load's options (a stored value
        // whose option left the data would select nothing) and date selections are
        // re-resolved from today — the store holds intent ("Last 30 days"), never dates.
        const sticky = dashboardId ? loadStickyFilters(getStickyStorage(), dashboardId) : null;
        const stickyOverlay: SlicerState = {};
        const stickySelections: Record<string, DateRangeSelection> = {};
        if (sticky) {
          for (const slicer of contentSpec.slicers ?? []) {
            if (slicer.type === "single_select") {
              const value = sticky.values[slicer.name];
              const valid =
                typeof value === "string"
                  ? resolveLiteralSingleDefault(
                      value,
                      newResolvedOptions[slicer.name] ?? slicer.options ?? []
                    )
                  : null;
              if (valid !== null) {
                stickyOverlay[slicer.name] = valid;
              }
            } else if (slicer.type === "multi_select") {
              const value = sticky.values[slicer.name];
              const valid = Array.isArray(value)
                ? resolveLiteralMultiDefault(value, newResolvedOptions[slicer.name] ?? [])
                : null;
              if (valid !== null) {
                stickyOverlay[slicer.name] = valid;
              }
            } else if (slicer.type === "date_range") {
              const selection = sticky.dateSelections[slicer.name];
              if (selection) {
                stickyOverlay[slicer.name] = resolveSelection(selection);
                stickySelections[slicer.name] = selection;
              }
            }
            // personalized_view is never persisted, so never restored.
          }
        }

        // React 17 does NOT batch state updates in this async context — unwrapped, every
        // set* below renders and flushes effects on its own, so effects observe half-committed
        // combinations. That is not hypothetical: with defaults committed before slicer state,
        // the "merge new slicers into state" effect seeded every name from the defaults first,
        // and the seeding below then kept those values — silently discarding the sticky
        // overlay. One batch = one render = effects only ever see the finished commit.
        unstable_batchedUpdates(() => {
          setAllTimeDateRanges(newAllTimeDateRanges);
          setResolvedOptions(newResolvedOptions);
          defaultDateRangeSelectionsRef.current = newDateRangeSelections;
          // Keep a selection the viewer already applied; a re-init (slicer reorder, edit) must
          // not silently relabel their choice back to the configured default. Sticky selections
          // only fill first-load gaps for the same reason.
          setDateRangeSelections(prev => {
            const next: Record<string, DateRangeSelection | null> = {};
            for (const name of Object.keys(newDateRangeSelections)) {
              next[name] =
                name in prev ? prev[name] : stickySelections[name] ?? newDateRangeSelections[name];
            }
            return next;
          });
          // Preserve existing slicer values when possible.
          //
          // This prevents a slicer *reorder* (which changes the slicers array reference) from
          // resetting slicerState and triggering every block to re-render/clear, which can
          // temporarily look like a blank dashboard while render functions run.
          //
          // Enqueued before setDefaultSlicerState so that even outside a batch the "merge new
          // slicers into state" effect never sees new defaults with unseeded state (the exact
          // interleaving that once overwrote the sticky overlay with defaults).
          const previousDefaults = defaultSlicerStateRef.current;
          setSlicerState(prev => {
            const slicers = contentSpec.slicers ?? [];
            const activeNames = new Set(slicers.map(s => s.name));

            let changed = false;
            const next: SlicerState = {};

            for (const name of activeNames) {
              const prevVal = prev[name];
              const untouched =
                prevVal !== undefined &&
                name in previousDefaults &&
                JSON.stringify(prevVal) === JSON.stringify(previousDefaults[name]);
              // An untouched slicer whose init failed this pass has no new default; it keeps
              // its last good value rather than being cleared to undefined.
              const nextVal =
                prevVal !== undefined && !untouched
                  ? prevVal
                  : untouched
                  ? newDefaultSlicerState[name] ?? prevVal
                  : stickyOverlay[name] ?? newDefaultSlicerState[name];
              next[name] = nextVal;

              if (!(name in prev)) {
                changed = true;
              } else if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
                changed = true;
              }
            }

            // Drop slicers that no longer exist in the spec.
            for (const prevName of Object.keys(prev)) {
              if (!activeNames.has(prevName)) {
                changed = true;
                break;
              }
            }

            return changed ? next : prev;
          });
          setDefaultSlicerState(newDefaultSlicerState);
          setSlicerInitErrors(newSlicerInitErrors);
          appliedDefaultsFingerprintRef.current = defaultsFingerprint;
          stickyDashboardIdRef.current = dashboardId ?? null;
          setIsInitialized(true);
        });

        const totalMs = Date.now() - runtimeInitStart;
        recordCompletedSpan({
          name: "web_dashboard.runtime_init",
          op: LOAD_SPAN_OP,
          startMs: runtimeInitStart,
          endMs: Date.now(),
          forceTransaction: true,
          attributes: {
            dashboard_id: dashboardId,
            total_ms: totalMs,
            drop_ms: dropMs,
            create_ms: createMs,
            slicer_init_ms: slicerInitMs,
            num_slicers: numSlicers,
            temp_objects_created: tempObjectsCreated,
            temp_objects_materialized: tempObjectsMaterialized,
            materialize_temp_views: materializeTempViews,
          },
        });
        addPerfBreadcrumb("runtime_init_done", {
          dashboard_id: dashboardId,
          total_ms: totalMs,
          slicer_init_ms: slicerInitMs,
          num_slicers: numSlicers,
          temp_objects_created: tempObjectsCreated,
          temp_objects_materialized: tempObjectsMaterialized,
        });
      }
    };

    initializeTempViews();

    return () => {
      cancelled = true;
    };
  }, [initFingerprint, initNonce, runSQL, enabled, materializeTempViews, userEmail, dashboardId]);

  // Reconcile defaults-only spec edits in place — the runtime's answer to "save filters as
  // default" (and its undo/rollback) without flashing every block back to a skeleton. The
  // init effect deliberately doesn't re-run for these (its fingerprint excludes
  // default_value/default_selection), so this effect recomputes defaultSlicerState against
  // state the last init already produced: literal values validate against resolvedOptions
  // and date selections re-resolve from today, no SQL involved. Current slicerState is
  // untouched — the viewer keeps what they're looking at; only what Reset returns to (and
  // the Save button's diff) changes. A default that cannot resolve from literals (a
  // JS-function default a literal no longer shadows) escalates to a full re-init via
  // initNonce; no loop, because that init records the fingerprint it consumed.
  useEffect(() => {
    if (!isInitialized || appliedDefaultsFingerprintRef.current === defaultsFingerprint) {
      return;
    }
    const nextDefaults: SlicerState = {};
    const nextDefaultSelections: Record<string, DateRangeSelection | null> = {};
    for (const slicer of contentSpec.slicers ?? []) {
      if (slicer.type === "date_range") {
        const selection = slicer.default_selection;
        if (selection) {
          nextDefaults[slicer.name] = resolveSelection(selection);
          nextDefaultSelections[slicer.name] = selection;
        } else if (slicer.get_default_value_js_function) {
          // Data-derived default: needs SQL, which only a full init runs.
          setInitNonce(n => n + 1);
          return;
        } else {
          nextDefaults[slicer.name] = {
            startDate: new Date(ALL_TIME_START.getTime()),
            endDate: new Date(ALL_TIME_END.getTime()),
          };
          nextDefaultSelections[slicer.name] = null;
        }
      } else if (slicer.type === "single_select") {
        const literal = resolveLiteralSingleDefault(
          slicer.default_value,
          resolvedOptions[slicer.name] ?? slicer.options ?? []
        );
        if (literal === null) {
          setInitNonce(n => n + 1);
          return;
        }
        nextDefaults[slicer.name] = literal;
      } else if (slicer.type === "multi_select") {
        const multiOptions = resolvedOptions[slicer.name] ?? [];
        const literal = slicer.default_all
          ? multiOptions.map(o => o.value)
          : resolveLiteralMultiDefault(slicer.default_value, multiOptions);
        if (literal === null) {
          setInitNonce(n => n + 1);
          return;
        }
        nextDefaults[slicer.name] = literal;
      }
      // personalized_view: its default is the viewer identity, which a defaults edit never
      // touches — the value already in defaultSlicerState stays.
    }
    appliedDefaultsFingerprintRef.current = defaultsFingerprint;
    defaultDateRangeSelectionsRef.current = {
      ...defaultDateRangeSelectionsRef.current,
      ...nextDefaultSelections,
    };
    setDefaultSlicerState(prev => ({ ...prev, ...nextDefaults }));
  }, [defaultsFingerprint, isInitialized, contentSpec.slicers, resolvedOptions]);

  // Set slicer value. `selection` is only passed by the date picker, which knows the structured
  // intent behind the dates it just committed.
  const setSlicerValue = useCallback(
    (slicerName: string, value: SlicerValue, selection?: DateRangeSelection | null) => {
      setSlicerState(prev => ({
        ...prev,
        [slicerName]: value,
      }));
      if (selection !== undefined) {
        setDateRangeSelections(prev => ({ ...prev, [slicerName]: selection }));
      }
    },
    []
  );

  // Compare current slicer state to default slicer state to determine if slicers are active
  useEffect(() => {
    const computeNewIsSlicersActive = () => {
      for (const [slicerName, currentValue] of Object.entries(slicerState)) {
        const defaultValue = defaultSlicerState[slicerName];
        if (JSON.stringify(currentValue) !== JSON.stringify(defaultValue)) {
          return true;
        }
      }
      return false;
    };
    const newIsSlicersActive = computeNewIsSlicersActive();
    if (newIsSlicersActive !== isSlicersActive) {
      startViewTransition(() => {
        setIsSlicersActive(newIsSlicersActive);
      });
    }
  }, [slicerState, defaultSlicerState, startViewTransition]);

  // Persist the viewer's filter choices so this dashboard reopens on their last view. Only
  // diffs from the defaults are written, and a state back at the defaults REMOVES the key —
  // so resetting is also forgetting. Gated on stickyDashboardIdRef (see its declaration)
  // so a mid-switch commit can't write one dashboard's filters under another's key. This
  // effect is declared after the init effect on purpose: within a commit the init effect
  // nulls the ref first, closing that window.
  useEffect(() => {
    if (!dashboardId || stickyDashboardIdRef.current !== dashboardId) {
      return;
    }
    const snapshot = buildStickySnapshot(
      contentSpec.slicers ?? [],
      slicerState,
      defaultSlicerState,
      dateRangeSelections
    );
    saveStickyFilters(getStickyStorage(), dashboardId, snapshot);
  }, [dashboardId, contentSpec.slicers, slicerState, defaultSlicerState, dateRangeSelections]);

  // Reset slicers to default values
  const resetSlicers = useCallback(() => {
    startViewTransition(() => {
      setSlicerState(defaultSlicerState);
      setDateRangeSelections(defaultDateRangeSelectionsRef.current);
      setIsSlicersActive(false);
    });
  }, [defaultSlicerState, startViewTransition]);

  // Register a cleanup function for a block
  const registerBlockCleanup = useCallback((blockId: string, cleanup: CleanupFunction) => {
    cleanupFunctionsRef.current[blockId] = cleanup;
  }, []);

  // Execute a block's render function
  const executeBlockRender = useCallback(
    async (blockId: string, renderFn: RenderFunction, cardElement: HTMLElement) => {
      // Store for future refreshes
      renderFunctionsRef.current[blockId] = { renderFn, cardElement };

      // Call existing cleanup
      const existingCleanup = cleanupFunctionsRef.current[blockId];
      if (existingCleanup) {
        try {
          await existingCleanup();
        } catch (error) {
          console.error(`Cleanup error for block ${blockId}:`, error);
        }
        delete cleanupFunctionsRef.current[blockId];
      }

      // Execute render function
      try {
        const cleanup = await renderFn(
          cardElement,
          normalizeSlicerStateForRealm(slicerState, targetWindow)
        );
        if (cleanup) {
          cleanupFunctionsRef.current[blockId] = cleanup;
        }
      } catch (error) {
        console.error(`Render error for block ${blockId}:`, error);
      }
    },
    [slicerState, targetWindow]
  );

  // Refresh a specific block
  const refreshBlock = useCallback(
    (blockId: string) => {
      setBlockRefreshTriggers(prev => ({
        ...prev,
        [blockId]: (prev[blockId] || 0) + 1,
      }));
      onBlockRefresh?.(blockId);
    },
    [onBlockRefresh]
  );

  // Refresh all blocks
  const refreshAllBlocks = useCallback(() => {
    const newTriggers: Record<string, number> = {};
    for (const blockId of Object.keys(renderFunctionsRef.current)) {
      newTriggers[blockId] = (blockRefreshTriggers[blockId] || 0) + 1;
      onBlockRefresh?.(blockId);
    }
    setBlockRefreshTriggers(prev => ({ ...prev, ...newTriggers }));
  }, [blockRefreshTriggers, onBlockRefresh]);

  // Build slicer -> block dependency map
  const slicerBlockDeps = useMemo(() => {
    const deps: Record<string, string[]> = {};
    for (const block of contentSpec.blocks) {
      if (block.type === "metric_card" || block.type === "chart" || block.type === "data_table") {
        for (const slicerName of block.depends_on_slicers) {
          if (!deps[slicerName]) {
            deps[slicerName] = [];
          }
          if (!deps[slicerName].includes(block.block_id)) {
            deps[slicerName].push(block.block_id);
          }
        }
      }
    }
    return deps;
  }, [contentSpec.blocks]);

  // When slicer state changes, refresh dependent blocks
  const prevSlicerStateRef = useRef<SlicerState>(slicerState);
  useEffect(() => {
    const prev = prevSlicerStateRef.current;
    prevSlicerStateRef.current = slicerState;

    // Find which slicers changed
    const changedSlicers: string[] = [];
    for (const slicerName of Object.keys(slicerState)) {
      const prevValue = prev[slicerName];
      const newValue = slicerState[slicerName];
      if (JSON.stringify(prevValue) !== JSON.stringify(newValue)) {
        changedSlicers.push(slicerName);
      }
    }

    // Refresh dependent blocks
    const blocksToRefresh = new Set<string>();
    for (const slicerName of changedSlicers) {
      for (const blockId of slicerBlockDeps[slicerName] || []) {
        blocksToRefresh.add(blockId);
      }
    }

    if (blocksToRefresh.size > 0) {
      setBlockRefreshTriggers(prev => {
        const newTriggers = { ...prev };
        for (const blockId of blocksToRefresh) {
          newTriggers[blockId] = (prev[blockId] || 0) + 1;
        }
        return newTriggers;
      });
    }
  }, [slicerState, slicerBlockDeps]);

  // Cleanup all blocks on unmount
  useEffect(() => {
    return () => {
      for (const cleanup of Object.values(cleanupFunctionsRef.current)) {
        try {
          cleanup();
        } catch (error) {
          console.error("Cleanup error on unmount:", error);
        }
      }
    };
  }, []);

  return {
    slicerState,
    defaultSlicerState,
    allTimeDateRanges,
    resolvedOptions,
    dateRangeSelections,
    setSlicerValue,
    resetSlicers,
    isSlicersActive,
    runSQL,
    registerBlockCleanup,
    executeBlockRender,
    refreshBlock,
    refreshAllBlocks,
    isInitialized,
    slicerInitErrors,
  };
}

export default useDashboardRuntime;
